import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataListDefault, DataListResponse, WorkTab } from "types/ui/card-item";
import TabsLayout from "components/tabs-layout";
import BoardBackground from "components/board/board-bg";
import Table, { tableHeader } from "components/table";
import { IconDownload, IconFolderOpen } from "@tabler/icons-react";
import ActionButton from "components/table/action-btn";
import { oneLine } from "components/table/table-parts";
import {
  DocumentListResponse,
  DocumentProps,
  DocumentFilters,
  DocumentFiltersDefault,
  DocumentTableResponse,
} from "types/document";
import { useSharedState } from "state";
import { StatusEnum } from "types/dict";
import { exportDocumentsToExcel, getDocuments, getDocumentsTable } from "api/document";

export default function DocumentsPage() {
  const {
    documents,
    setDocuments,
    documentsTable,
    setDocumentsTable,
    currentPageTab,
    searchQuery,
    handleSearch,
    handleClearFilters,
    setOffset,
    firstEntry,
    filtersList,
    filtersCount,
    user,
    getDocumentsData,
    getDocumentsTableData,
    updateFirstEntry,
    notify,
  } = useSharedState();
  const navigate = useNavigate();
  const { locationName } = useParams();

  const [documentCards, setDocumentCards] = useState<DataListResponse>(DataListDefault);
  const [localDocuments, setLocalDocuments] = useState<DocumentListResponse>(documents);
  const [localDocumentsTable, setLocalDocumentsTable] = useState(documentsTable);
  const [documentsLength, setDocumentsLength] = useState(0);

  const selectedLocation = user.locations.find((loc) => loc.location === locationName);

  useEffect(() => {
    setLocalDocuments(documents);
  }, [documents]);

  useEffect(() => {
    setLocalDocumentsTable(documentsTable);
  }, [documentsTable]);

  useEffect(() => {
    setDocumentCards(convertDocumentsToCards(localDocuments));
  }, [localDocuments]);

  useEffect(() => {
    // When location changes or becomes null, fetch appropriate data
    const fetchData = async () => {
      try {
        if (locationName) {
          // Fetch filtered data for specific location
          const filters: DocumentFilters = {
            ...DocumentFiltersDefault,
            locations: [locationName],
          };

          if (currentPageTab === "cards") {
            const res: DocumentListResponse = await getDocuments(filters);
            setDocuments(res);
            setLocalDocuments(res);
            setDocumentsLength(Object.values(res).reduce((a, b) => a + b.total_count, 0));
          } else if (currentPageTab === "table") {
            const tableFilters = { ...filters, offset: 0 };
            const res: DocumentTableResponse = await getDocumentsTable(tableFilters);
            setDocumentsTable(res);
            setLocalDocumentsTable(res);
          }
        } else {
          // Fetch unfiltered data when no location is selected
          if (currentPageTab === "cards") {
            await getDocumentsData();
          } else if (currentPageTab === "table") {
            await getDocumentsTableData(0);
          }
        }
        setOffset(0);
        updateFirstEntry(1);
      } catch (error: any) {
        console.error("Error fetching documents data:", error);
      }
    };

    fetchData();
  }, [locationName, currentPageTab]);

  const onDocumentPageChange = async (offset: number) => {
    setOffset(offset);
    updateFirstEntry(offset + 1);

    const filters: DocumentFilters = {
      ...DocumentFiltersDefault,
      locations: locationName ? [locationName] : undefined,
      offset,
    };

    try {
      const res: DocumentTableResponse = await getDocumentsTable(filters);
      setLocalDocumentsTable(res);
    } catch (error: any) {
      console.error("Error fetching paginated documents:", error);
    }
  };

  const handleExportDocument = async () => {
    try {
      const filters: DocumentFilters = {
        ...DocumentFiltersDefault,
        search: searchQuery || undefined,
        locations: locationName ? [locationName] : undefined,
        from_date: filtersList.from_date || undefined,
        to_date: filtersList.to_date || undefined,
        contragent_id: Number(filtersList.contragent_id) || undefined,
        template_name: filtersList.template_name || undefined,
        creator_name: filtersList.creator_name || undefined,
        status: filtersList.status ? [filtersList.status] : undefined,
      };

      const res = await exportDocumentsToExcel(filters);

      // Create blob URL from the binary data
      const blob = new Blob([res]);
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      const fileName = `documents_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.xlsx`;

      link.href = blobUrl;
      link.download = fileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL to free memory
      URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при экспорте документов");
    }
  };

  const formattedCards = (documentsByStatus: DocumentProps[]) => {
    return documentsByStatus?.map((document) => ({
      id: document.document_request_id,
      title: (
        <>
          <div>
            {document.template_name} №{document.lease_number}
          </div>
          <div>{document.contragent}</div>
        </>
      ),
      description: document.description,
      status: document.status,
      dueDate: document.request_date,
      created_by: document.creator_name,
      assigned_to:
        document.approver_name && document.approver_name !== "unknown" ? document.approver_name : document.creator_name,
      priority: undefined,
    }));
  };

  const convertDocumentsToCards = (documents: DocumentListResponse): DataListResponse => {
    return {
      "0": {
        data: formattedCards(documents["0"]?.data),
        total_count: documents["0"]?.total_count,
      },
      "1": {
        data: formattedCards(documents["1"]?.data),
        total_count: documents["1"]?.total_count,
      },
      "4": {
        data: formattedCards(documents["4"]?.data),
        total_count: documents["4"]?.total_count,
      },
      "2": {
        data: formattedCards(documents["2"]?.data),
        total_count: documents["2"]?.total_count,
      },
    };
  };

  const getStatusTitle = (status: number) => {
    switch (status) {
      case StatusEnum.TO_DO:
        return "К выполнению";
      case StatusEnum.IN_PROGRESS:
        return "В работе";
      case StatusEnum.COMPLETED:
        return "Завершён";
      case StatusEnum.REJECTED:
        return "Отклонён";
      case StatusEnum.IN_EXECUTION:
        return "На подписании";
      default:
        return "-";
    }
  };

  const documentsTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: false },
    { header_text: "Информация по договору", narrow: false, wrap: false },
    { header_text: "Шаблон", narrow: false, wrap: false },
    { header_text: "Контрагент", narrow: false, wrap: false },
    { header_text: "Номер договора", narrow: false, wrap: false },
    { header_text: "Ответственный", narrow: false, wrap: false },
    { header_text: "Создатель", narrow: false, wrap: false },
    { header_text: "Статус", narrow: false, wrap: false },
    { header_text: "Действия", narrow: true, wrap: false },
  ];

  const documentsList = (
    <>
      {localDocumentsTable.data.map((document, index) => (
        <tr key={index}>
          <td>{oneLine((firstEntry + index).toString())}</td>
          <td>{oneLine(document.description || "-")}</td>
          <td>{oneLine(document.template_name || "-")}</td>
          <td>{oneLine(document.contragent || "-")}</td>
          <td>{oneLine(document.lease_number || "-")}</td>
          <td>{oneLine(document.approver_name || "-")}</td>
          <td>{oneLine(document.creator_name || "-")}</td>
          <td>{oneLine(getStatusTitle(document.status))}</td>
          <td>
            <div className="btn-list flex-nowrap">
              <ActionButton
                icon={<IconFolderOpen className="icon" />}
                title="Подробнее"
                action={() => navigate(`${document.document_request_id}`)}
                color={"primary"}
                modal={false}
              />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  const documentsTableComponent = Table(documentsTableHeaders, documentsList, true, {
    total: localDocumentsTable.total_count,
    updateInfo: onDocumentPageChange,
  });

  const document_tabs: WorkTab[] = [
    {
      id: "cards",
      title: "Договоры по статусам",
      component: <BoardBackground data={documentCards} />,
    },
    { id: "table", title: "Таблица", component: documentsTableComponent },
  ];

  const getHeaderText = () => {
    if (selectedLocation) {
      return `Договоры на утверждение - ${selectedLocation.location}`;
    }
    return "Договоры на утверждение";
  };

  const smallButtons = [
    {
      id: "excel",
      label: "Экспорт в Excel",
      icon: <IconDownload />,
      variant: "primary" as const,
      onClick: handleExportDocument,
      disabled: documentsLength === 0 && localDocumentsTable.total_count === 0,
    },
  ];

  return (
    <TabsLayout
      tabs={document_tabs}
      header={getHeaderText()}
      subheaderText={`Количество договоров: ${localDocumentsTable.total_count}`}
      buttonText="Новый договор"
      smallButtons={smallButtons}
    />
  );
}
